import { cityTurn } from "../cityCommand/cityDuty";
import { isCityCommand, kingPresentIn } from "../cityCommand/cityVerbs";
import { isRushed } from "../court/general";
import { commandRefusal, spendWill } from "../court/king";
import type { AxialCoord } from "../hex";
import { isWorkingCityOf, workingCities } from "../land/cities";
import { percentOf } from "../percent";
import { findHex, isOwnGround, patchHexAt, unitAt, unitsOf } from "../run/board";
import { recruitsOf, spend, spendRecruits, supplyOf } from "../run/pools";
import { armyUnitsOf } from "../run/seats";
import type { RunRules } from "../types/rules";
import type { FactionId, HexState, Role, RunState, UnitKind } from "../types/state";
import { floodFrom } from "./routes";
import { addUnit, newUnit } from "./unit";

/**
 * The army: prices (the levy), the recruit a soldier costs, the army's room, training in a city's
 * yard and the finished units stepping out. A unit's stats and its record are ./unit.ts.
 */

/** Supply the faction pays for its next unit of this kind: the base price under the levy. */
export function unitPrice(state: RunState, faction: FactionId, kind: UnitKind): number {
  const { cost } = state.rules.units[kind];
  const levy = state.rules.levy;
  if (!levy) {
    return cost;
  }
  const army = unitCount(state, faction);
  return percentOf(cost, 100 + levy.stepPercent * army + levy.curvePercent * army * army);
}

/**
 * Ticks the faction's next unit of this kind trains for: the build time, shortened by the rally,
 * and in its city by the King's rush or, for a city-command King, by his presence there (CC1).
 */
export function unitBuildTicks(
  state: RunState,
  faction: FactionId,
  kind: UnitKind,
  city?: AxialCoord,
): number {
  const { buildTicks } = state.rules.units[kind];
  const levy = state.rules.levy;
  const rally = levy
    ? Math.min(100, levy.rallyPercent + levy.rallyStepPercent * unitCount(state, faction))
    : 100;
  const rush =
    city && isRushed(state, faction, city)
      ? (state.rules.court?.general.rushPercent ?? 100)
      : city && kingPresentIn(state, faction, city)
        ? (state.rules.cityCommand?.presencePercent ?? 100)
        : 100;
  if (rally === 100 && rush === 100) {
    return buildTicks;
  }
  return Math.max(1, Math.floor((buildTicks * rally * rush) / 10_000));
}

/** Recruits a soldier costs (A53); 0 without the population block. */
export function recruitCost(rules: RunRules): number {
  return rules.population?.unitCost ?? 0;
}

/**
 * What the pool lacks for one soldier of this kind now: its price first, then its recruit;
 * undefined when it holds both. The recruit verb, the yard's refusal and the forge read it.
 */
export function trainShortfall(
  state: RunState,
  faction: FactionId,
  kind: UnitKind,
): "supply" | "recruit" | undefined {
  if (supplyOf(state, faction) < unitPrice(state, faction, kind)) {
    return "supply";
  }
  return recruitsOf(state, faction) < recruitCost(state.rules) ? "recruit" : undefined;
}

/** Upkeep room: units a faction may field, from its working cities. */
export function unitRoom(state: RunState, faction: FactionId): number {
  return workingCities(state, faction).length * state.rules.unitsPerCity;
}

/**
 * Units a faction has, built or in progress in its cities: what the room, the levy and the
 * rally read. A soldier seat's unit, held or not, is the account's and is not counted (A61, K1):
 * counted, an idle human's cost its kingdom a third of a one-city army.
 */
export function unitCount(state: RunState, faction: FactionId): number {
  const building = state.hexes.filter(
    (hex) => isOwnGround(hex, faction) && hex.unitBuild !== "none",
  ).length;
  return armyUnitsOf(state, faction).length + building;
}

/** True when the faction fields a unit of the kind or has one in training. */
export function hasKind(state: RunState, faction: FactionId, kind: UnitKind): boolean {
  return (
    unitsOf(state, faction).some((unit) => unit.kind === kind) ||
    state.hexes.some((hex) => isOwnGround(hex, faction) && hex.unitBuild === kind)
  );
}

/**
 * The recruit verb's precondition: an own working city with no build in progress, room for
 * one more unit, and the price in the pool.
 */
export function canBuildUnit(
  state: RunState,
  faction: FactionId,
  coord: AxialCoord,
  kind: UnitKind,
  issuer: Role = "king",
): boolean {
  const hex = findHex(state, coord);
  return (
    state.status === "running" &&
    isWorkingCityOf(hex, faction, state.tick) &&
    hex.unitBuild === "none" &&
    unitCount(state, faction) < unitRoom(state, faction) &&
    trainShortfall(state, faction, kind) === undefined &&
    commandRefusal(state, faction, coord, "train", issuer) === undefined
  );
}

/**
 * Start a unit in a city. The price leaves the pool at once; the unit appears after the
 * kind's build time on the city hex, or the nearest free own hex when the city is occupied.
 * Returns the same object when rejected.
 */
export function buildUnit(
  state: RunState,
  faction: FactionId,
  coord: AxialCoord,
  kind: UnitKind,
  issuer: Role = "king",
): RunState {
  if (!canBuildUnit(state, faction, coord, kind, issuer)) {
    return state;
  }
  const paid = spendWill(
    spendRecruits(
      spend(state, faction, unitPrice(state, faction, kind)),
      faction,
      recruitCost(state.rules),
    ),
    faction,
    "train",
    issuer,
  );
  return patchHexAt(paid, coord, {
    unitBuild: kind,
    unitBuildReadyTick: state.tick + unitBuildTicks(state, faction, kind, coord),
  });
}

/** Where a finished unit stands: the city itself, else the nearest free own hex by breadth-first walk. */
export function spawnHexFor(state: RunState, city: HexState): AxialCoord | undefined {
  const own = (hex: HexState): boolean => !hex.razed && hex.owner === city.owner;
  for (const coord of floodFrom(state, city.coord, own)) {
    if (unitAt(state, coord) === undefined) {
      return coord;
    }
  }
  return undefined;
}

/**
 * Step 1c: a unit whose build time has passed appears; a city with nowhere to put it waits. A
 * unit born in a city-command kingdom's city marches in that city's column, and under an arrow
 * takes its duty at once (CC1).
 */
export function completeUnitBuilds(state: RunState): RunState {
  let next = state;
  const answering: { readonly faction: FactionId; readonly city: AxialCoord }[] = [];
  for (const city of state.hexes) {
    if (
      city.unitBuild === "none" ||
      city.unitBuildReadyTick > state.tick ||
      city.owner === "none"
    ) {
      continue;
    }
    const at = spawnHexFor(next, city);
    if (!at) {
      continue;
    }
    const columned = isCityCommand(state, city.owner);
    const unit = {
      ...newUnit(next, city.unitBuild, city.owner, at),
      ...(columned ? { home: city.coord } : {}),
    };
    next = patchHexAt(addUnit(next, unit), city.coord, {
      unitBuild: "none",
      unitBuildReadyTick: 0,
    });
    if (columned && city.arrow) {
      answering.push({ faction: city.owner, city: city.coord });
    }
  }
  for (const { faction, city } of answering) {
    next = cityTurn(next, faction, city);
  }
  return next;
}
